import React, { useState, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Skeleton,
  Grid,
  Alert as MuiAlert,
  Chip,
  Card,
  CardContent,
  Avatar,
  CircularProgress,
  Autocomplete,
  Button,
  CardActionArea,
  useTheme,
} from "@mui/material";
import {
  ArrowBack,
  Search,
  People,
  CalendarMonth,
  SportsSoccer,
  Star,
  Description,
  NoteAlt,
  PushPin,
  Today,
  Insights,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  useFollowedPlayers,
  useDiscoveredMatches,
  useUnfollowPlayer,
  useMatchNotes,
  useWatchMatch,
  useUnwatchMatch,
  useWatchedMatches,
  useScoutingAgenda,
} from "../hooks/queries/useScouting";
import { getActaByTemporadaAndCodActa } from "../services/actasService";
import FollowedPlayerCard from "../components/scouting/FollowedPlayerCard";
import PlayerNotesDialog from "../components/scouting/PlayerNotesDialog";
import MatchNoteDialog from "../components/scouting/MatchNoteDialog";
import ScoutingAgendaView from "../components/scouting/ScoutingAgendaView";
import ScoutingDashboard from "../components/scouting/ScoutingDashboard";
import ScoutingEmptyState from "../components/scouting/ScoutingEmptyState";
import colors from "../design-system/colors";
import spacing from "../design-system/spacing";
import usePageTitle from '../hooks/usePageTitle';
import usePageTour from '../hooks/usePageTour';
import { useAuth } from "../context/AuthContext";
import "./ScoutingPageModern.css";

export const limitUpcomingMatchesByPlayer = (matches = [], maximumMatches = 3) => {
  const playerMatchCounts = new Map();
  let matchesWithoutPlayers = 0;

  return matches.filter((match) => {
    const playerIds = [
      ...new Set(
        (match.relatedPlayers || [])
          .map((player) => player.jugadorId)
          .filter((playerId) => playerId != null)
          .map(String)
      ),
    ];

    if (playerIds.length === 0) {
      if (matchesWithoutPlayers >= maximumMatches) return false;
      matchesWithoutPlayers += 1;
      return true;
    }

    const shouldInclude = playerIds.some(
      (playerId) => (playerMatchCounts.get(playerId) || 0) < maximumMatches
    );
    if (!shouldInclude) return false;

    playerIds.forEach((playerId) => {
      playerMatchCounts.set(playerId, (playerMatchCounts.get(playerId) || 0) + 1);
    });
    return true;
  });
};

// ─── Match Card for discovered matches ────────────────────────────────────────
const DiscoveredMatchCard = ({ match, navigate, onOpenNote, matchNotesMap, watchedSet, onWatch, onUnwatch }) => {
  const { t, i18n } = useTranslation('scouting');
  const [loadingActa, setLoadingActa] = useState(false);
  const fechaDate = match.fechaISO ? new Date(match.fechaISO) : null;
  const fechaStr = fechaDate
    ? fechaDate.toLocaleDateString(i18n.language, {
        weekday: "short", day: "numeric", month: "short", year: "numeric",
      })
    : t('matchCard.noDate');
  const horaStr = match.hora || "";
  const horaDisplay = horaStr || t('matchCard.timeUnknown');
  const hasScore = match.golesCasa != null && match.golesVisitante != null;
  const partidoKey = String(match.codPartido || match.codacta || `${match.codEquipoLocal}_${match.codEquipoVisitante}_${match.fecha}`);

  const handleVerActa = async () => {
    if (!match.codacta || !match.idTemporada) return;
    setLoadingActa(true);
    try {
      const actaList = await getActaByTemporadaAndCodActa(
        match.idTemporada, match.codacta, navigate
      );
      const acta = actaList?.[0];
      if (acta) {
        navigate("/actaPartido", {
          state: { partido: match, acta, minutosJuego: acta.minutosJuego || 80 },
        });
      }
    } catch {
      // silently handled
    } finally {
      setLoadingActa(false);
    }
  };

  // Check which players already have notes for this match
  const playersWithNotes = useMemo(() => {
    if (!matchNotesMap || !match.relatedPlayers) return new Set();
    const s = new Set();
    match.relatedPlayers.forEach((p) => {
      const key = `${p.jugadorId}_${partidoKey}`;
      if (matchNotesMap[key]) s.add(String(p.jugadorId));
    });
    return s;
  }, [matchNotesMap, match.relatedPlayers, partidoKey]);

  return (
    <Box className="sc-match">
      <Card
        className="sc-panel"
        sx={{
          height: "100%",
        }}
      >
        <CardContent sx={{ py: 1.5, px: 2, "&:last-child": { pb: 1.5 } }}>
          {/* Competición + Categoría */}
          {(match.competicion || match.categoria) && (
            <Typography className="sc-muted" variant="caption" sx={{ display: "block", mb: 0.5 }}>
              {[match.categoria, match.competicion].filter(Boolean).join(" · ")}
            </Typography>
          )}
          {/* Fecha + Jornada header */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
            <Typography className="sc-match-date" variant="caption" fontWeight={600}>
              {fechaStr} · {horaDisplay}
            </Typography>
            {match.jornada && (
              <Chip
                label={`J${match.jornada}`}
                size="small"
                sx={{
                  height: 20, fontSize: "0.65rem", fontWeight: 700,
                  backgroundColor: colors.primary[50], color: colors.primary[700],
                }}
              />
            )}
          </Box>

          {/* Equipos con resultado */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            {match.escudoEquipoLocal && (
              <Avatar src={match.escudoEquipoLocal} sx={{ width: 22, height: 22 }} />
            )}
            <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
              {match.equipoLocal || t('matchCard.local')}
            </Typography>
            {hasScore && (
              <Typography variant="body2" fontWeight={800} color={colors.primary[700]} sx={{ minWidth: 16, textAlign: "center" }}>
                {match.golesCasa}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {match.escudoEquipoVisitante && (
              <Avatar src={match.escudoEquipoVisitante} sx={{ width: 22, height: 22 }} />
            )}
            <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
              {match.equipoVisitante || t('matchCard.visitor')}
            </Typography>
            {hasScore && (
              <Typography variant="body2" fontWeight={800} color={colors.primary[700]} sx={{ minWidth: 16, textAlign: "center" }}>
                {match.golesVisitante}
              </Typography>
            )}
          </Box>

          {/* Jugadores seguidos con botón de notas */}
          {match.relatedPlayers?.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1, pt: 1, borderTop: `1px solid ${colors.neutral[100]}` }}>
              {match.relatedPlayers.map((p) => (
                <Button
                  key={p.jugadorId}
                  className="sc-player-note"
                  data-has-note={playersWithNotes.has(String(p.jugadorId))}
                  startIcon={<NoteAlt />}
                  aria-label={t('workspace.openPlayerNote', { name: p.jugadorNombre })}
                  onClick={() => onOpenNote(match, p)}
                >
                  {p.jugadorNombre}
                </Button>
              ))}
            </Box>
          )}

          {/* Action buttons */}
          <Box className="sc-match-actions" sx={{ display: "flex", gap: 1, mt: 1.5, pt: 1, borderTop: `1px solid ${colors.neutral[100]}`, flexWrap: "wrap" }}>
            {match.codacta && fechaDate && fechaDate < new Date() && (
              <Chip
                icon={loadingActa
                  ? <CircularProgress size={14} sx={{ color: `${colors.primary[700]} !important` }} />
                  : <Description sx={{ fontSize: "16px !important" }} />}
                label={t('matchCard.viewActa')}
                size="small"
                clickable
                disabled={loadingActa}
                onClick={handleVerActa}
                sx={{
                  fontWeight: 600, fontSize: "0.7rem",
                  backgroundColor: colors.primary[50], color: colors.primary[700],
                  "&:hover": { backgroundColor: colors.primary[100] },
                }}
              />
            )}
            {match.relatedPlayers?.length === 1 && (
              <Chip
                icon={<NoteAlt sx={{ fontSize: "16px !important" }} />}
                label={t('matchCard.notes')}
                size="small"
                clickable
                onClick={() => onOpenNote(match, match.relatedPlayers[0])}
                sx={{
                  fontWeight: 600, fontSize: "0.7rem",
                  backgroundColor: colors.neutral[100], color: colors.neutral[700],
                  "&:hover": { backgroundColor: 'action.hover' },
                }}
              />
            )}
            {/* Watch/Unwatch button — only for upcoming matches */}
            {!hasScore && (() => {
              const isWatched = watchedSet?.has(partidoKey);
              return (
                <Button
                  startIcon={<PushPin sx={{ fontSize: "16px !important" }} />}
                  variant={isWatched ? "contained" : "outlined"}
                  size="small"
                  onClick={() => {
                    if (isWatched) {
                      onUnwatch?.(partidoKey);
                    } else {
                      onWatch?.(match, partidoKey);
                    }
                  }}
                  sx={{
                    minHeight: 28,
                    fontWeight: 700,
                    fontSize: "0.7rem",
                    textTransform: "none",
                    borderColor: isWatched ? colors.secondary[600] : colors.primary[400],
                    backgroundColor: isWatched ? colors.secondary[600] : "transparent",
                    color: isWatched ? colors.neutral[0] : colors.primary[700],
                    "&:hover": {
                      borderColor: isWatched ? colors.secondary[700] : colors.primary[600],
                      backgroundColor: isWatched ? colors.secondary[700] : colors.primary[50],
                    },
                  }}
                >
                  {isWatched ? t('matchCard.inAgenda') : t('matchCard.addToAgenda')}
                </Button>
              );
            })()}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

// ─── Player filter for Agenda/Partidos/Notas tabs ─────────────────────────────
const PlayerFilterChips = ({ players, selected, onChange }) => {
  const { t } = useTranslation('scouting');
  if (!players?.length) return null;

  const normalizedSelected = selected == null ? null : String(selected);
  const isSelected = (jugadorId) => normalizedSelected === String(jugadorId);

  if (players.length > 6) {
    const options = [
      { jugadorId: null, jugadorNombre: t('players.allPlayers') },
      ...players,
    ];
    const selectedOption = options.find((option) => (
      option.jugadorId == null
        ? normalizedSelected === null
        : String(option.jugadorId) === normalizedSelected
    )) || options[0];

    return (
      <Autocomplete
        size="small"
        disableClearable
        options={options}
        value={selectedOption}
        onChange={(_, value) => onChange(value?.jugadorId ?? null)}
        isOptionEqualToValue={(option, value) => String(option?.jugadorId) === String(value?.jugadorId)}
        getOptionLabel={(option) => option?.jugadorNombre || t('players.allPlayers')}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;
          return (
            <Box
              component="li"
              key={key}
              {...optionProps}
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              {option.jugadorId != null && (
                <Star sx={{ fontSize: 16, color: colors.warning?.[500] || "#f59e0b" }} />
              )}
              <Typography variant="body2" fontWeight={option.jugadorId == null ? 600 : 500}>
                {option.jugadorNombre}
              </Typography>
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('players.player')}
            placeholder={t('players.allPlayers')}
          />
        )}
        sx={{
          mb: 2,
          maxWidth: { xs: "100%", sm: 360 },
          "& .MuiOutlinedInput-root": {
            backgroundColor: 'background.default',
            "& fieldset": {
              borderColor: 'divider',
            },
            "&:hover fieldset": {
              borderColor: colors.primary[600],
            },
            "&.Mui-focused fieldset": {
              borderColor: colors.primary[600],
            },
          },
          "& .MuiInputLabel-root.Mui-focused": {
            color: colors.primary[600],
          },
        }}
      />
    );
  }

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 2 }}>
      <Chip
        label={t('players.all')}
        size="small"
        variant={normalizedSelected === null ? "filled" : "outlined"}
        onClick={() => onChange(null)}
        sx={{
          fontWeight: 600,
          ...(normalizedSelected === null
            ? { backgroundColor: colors.primary[600], color: "#fff" }
            : { borderColor: 'divider' }),
        }}
      />
      {players.map((p) => {
        const selectedPlayer = isSelected(p.jugadorId);
        return (
          <Chip
            key={p.jugadorId}
            icon={<Star sx={{ fontSize: "14px !important", color: selectedPlayer ? "#fff !important" : `${colors.warning?.[500] || "#f59e0b"} !important` }} />}
            label={p.jugadorNombre}
            size="small"
            variant={selectedPlayer ? "filled" : "outlined"}
            onClick={() => onChange(selectedPlayer ? null : p.jugadorId)}
            sx={{
              fontWeight: 500,
              ...(selectedPlayer
                ? { backgroundColor: colors.primary[600], color: "#fff" }
                : { borderColor: 'divider' }),
            }}
          />
        );
      })}
    </Box>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const ScoutingPageModern = () => {
  const theme = useTheme();
  const { user } = useAuth();
  usePageTitle('Scouting');
  usePageTour('scouting');
  const { t, i18n } = useTranslation('scouting');
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [search, setSearch] = useState("");
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [playerFilter, setPlayerFilter] = useState(null);
  const [matchNoteOpen, setMatchNoteOpen] = useState(false);
  const [matchNoteMatch, setMatchNoteMatch] = useState(null);
  const [matchNotePlayer, setMatchNotePlayer] = useState(null);

  const userId = user?.oid || user?.sub || user?.id || null;

  const {
    data: followedPlayers = [],
    isLoading: loadingPlayers,
    error: playersError,
  } = useFollowedPlayers(userId);

  const {
    data: discoveredData,
    isLoading: loadingDiscovered,
    error: discoveredError,
  } = useDiscoveredMatches(userId);

  const {
    data: matchNotes = [],
  } = useMatchNotes(userId);

  const {
    data: watchedMatches = [],
  } = useWatchedMatches(userId);

  const {
    data: agendaMatches = [],
  } = useScoutingAgenda(userId);

  const { mutate: watchMatchMut } = useWatchMatch();
  const { mutate: unwatchMatchMut } = useUnwatchMatch();

  const upcoming = useMemo(() => discoveredData?.upcoming || [], [discoveredData?.upcoming]);
  const recent = useMemo(() => discoveredData?.recent || [], [discoveredData?.recent]);

  const { mutate: unfollowPlayerMut } = useUnfollowPlayer();

  // Set of watched partidoIds for quick lookup
  const watchedSet = useMemo(() => {
    const s = new Set();
    watchedMatches.forEach((w) => s.add(String(w.partidoId)));
    return s;
  }, [watchedMatches]);

  // Build a lookup map: `${jugadorId}_${partidoId}` → note
  const matchNotesMap = useMemo(() => {
    const map = {};
    matchNotes.forEach((n) => {
      map[`${n.jugadorId}_${n.partidoId}`] = n;
    });
    return map;
  }, [matchNotes]);

  // Filtrar partidos por jugador seleccionado
  const filteredUpcoming = useMemo(() => {
    if (!playerFilter) return upcoming;
    return upcoming.filter((m) =>
      m.relatedPlayers?.some((p) => String(p.jugadorId) === String(playerFilter))
    );
  }, [upcoming, playerFilter]);

  const visibleUpcoming = useMemo(
    () => limitUpcomingMatchesByPlayer(filteredUpcoming, 3),
    [filteredUpcoming]
  );

  const filteredRecent = useMemo(() => {
    if (!playerFilter) return recent;
    return recent.filter((m) =>
      m.relatedPlayers?.some((p) => String(p.jugadorId) === String(playerFilter))
    );
  }, [recent, playerFilter]);

  const filteredMatchNotes = useMemo(() => {
    if (!playerFilter) return matchNotes;
    return matchNotes.filter((note) => String(note.jugadorId) === String(playerFilter));
  }, [matchNotes, playerFilter]);

  const filteredPlayers = useMemo(() => {
    if (!search) return followedPlayers;
    const term = search.toLowerCase();
    return followedPlayers.filter(
      (p) =>
        p.jugadorNombre?.toLowerCase().includes(term) ||
        p.equipoNombre?.toLowerCase().includes(term)
    );
  }, [followedPlayers, search]);

  const handleUnfollow = (player) => {
    if (!userId) return;
    unfollowPlayerMut({ userId, jugadorId: player.jugadorId });
  };

  const handleOpenNotes = (player) => {
    setSelectedPlayer(player);
    setNotesDialogOpen(true);
  };

  const handleCloseNotes = () => {
    setNotesDialogOpen(false);
    setSelectedPlayer(null);
  };

  const handleOpenMatchNote = useCallback((match, player) => {
    setMatchNoteMatch(match);
    setMatchNotePlayer(player);
    setMatchNoteOpen(true);
  }, []);

  const handleCloseMatchNote = useCallback(() => {
    setMatchNoteOpen(false);
    setMatchNoteMatch(null);
    setMatchNotePlayer(null);
  }, []);

  // Watch / Unwatch match for personal agenda
  const handleWatchMatch = useCallback((match, partidoKey) => {
    if (!userId) return;
    watchMatchMut({
      userId,
      partidoId: partidoKey,
      matchData: {
        equipoLocal: match.equipoLocal || "",
        equipoVisitante: match.equipoVisitante || "",
        equipoLocalId: match.codEquipoLocal || null,
        equipoVisitanteId: match.codEquipoVisitante || null,
        idTemporada: match.idTemporada || null,
        fecha: match.fechaISO || match.fecha || "",
        hora: match.hora || "",
        competicion: match.competicion || "",
        categoria: match.categoria || "",
        jornada: match.jornada || null,
        relatedPlayerIds: match.relatedPlayers?.map((p) => p.jugadorId) || [],
      },
    });
  }, [userId, watchMatchMut]);

  const handleUnwatchMatch = useCallback((partidoId) => {
    if (!userId) return;
    unwatchMatchMut({ userId, partidoId });
  }, [userId, unwatchMatchMut]);

  // Find existing note object for the dialog
  const existingMatchNote = useMemo(() => {
    if (!matchNoteMatch || !matchNotePlayer) return null;
    const partidoKey = String(matchNoteMatch.codPartido || matchNoteMatch.codacta ||
      `${matchNoteMatch.codEquipoLocal}_${matchNoteMatch.codEquipoVisitante}_${matchNoteMatch.fecha}`);
    const key = `${matchNotePlayer.jugadorId}_${partidoKey}`;
    return matchNotesMap[key] || null;
  }, [matchNoteMatch, matchNotePlayer, matchNotesMap]);

  const tabConfig = [
    {
      label: t('tabs.dashboard'),
      icon: <Insights fontSize="small" />,
      count: 0,
    },
    {
      label: t('tabs.players'),
      icon: <People fontSize="small" />,
      count: followedPlayers.length,
    },
    {
      label: t('tabs.agenda'),
      icon: <CalendarMonth fontSize="small" />,
      count: visibleUpcoming.length,
    },
    {
      label: t('tabs.recentMatches'),
      icon: <SportsSoccer fontSize="small" />,
      count: filteredRecent.length,
    },
    {
      label: t('tabs.myAgenda'),
      icon: <Today fontSize="small" />,
      count: agendaMatches.length,
    },
    {
      label: t('tabs.notes'),
      icon: <NoteAlt fontSize="small" />,
      count: filteredMatchNotes.length,
    },
  ];

  return (
    <Box className="scouting-workspace" data-theme={theme.palette.mode}>
      {/* Header */}
      <Box
        className="sc-header"
        data-tour="scouting-header"
      >
        <Box className="dv-page-heading sc-title-row">
            <Box sx={{ minWidth: 0 }}>
              <Typography component="h1" className="sc-title">
                {t('header.title')}
              </Typography>
              <Typography className="sc-muted">
                {t('workspace.subtitle')}
              </Typography>
            </Box>
          <Box className="sc-header-actions">
            <Button onClick={() => navigate(-1)} startIcon={<ArrowBack />}>
              {t('header.back')}
            </Button>
            <Button className="sc-primary" variant="contained" onClick={() => navigate("/rankingJugadores")} startIcon={<Search />}>
              {t('workspace.findPlayers')}
            </Button>
          </Box>
        </Box>

        {/* Tabs */}
        <Box data-tour="scouting-tabs">
          <Tabs
            className="sc-tabs"
            aria-label={t('workspace.navigation')}
            value={tab}
            onChange={(_, v) => { setTab(v); setPlayerFilter(null); }}
            textColor="primary"
            indicatorColor="primary"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              "& .MuiTab-root": {
                fontWeight: 600,
                textTransform: "none",
                minWidth: { xs: "auto", sm: 120 },
                px: { xs: 1.5, sm: 2 },
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
              },
              "& .MuiTab-iconWrapper": {
                display: { xs: "none", sm: "inline-flex" },
              },
              "& .MuiTabScrollButton-root": {
                color: colors.primary[600],
              },
            }}
          >
            {tabConfig.map((item, i) => (
              <Tab
                key={i}
                id={`scouting-tab-${i}`}
                aria-controls={`scouting-panel-${i}`}
                icon={item.icon}
                iconPosition="start"
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    {item.label}
                    {item.count > 0 && (
                      <span className="sc-tab-count">{item.count}</span>
                    )}
                  </Box>
                }
              />
            ))}
          </Tabs>
        </Box>
      </Box>

      {/* Content */}
      <Box className="sc-content" role="tabpanel" id={`scouting-panel-${tab}`} aria-labelledby={`scouting-tab-${tab}`}>
        <Box className="sc-section-heading">
          <Typography component="h2">{t(`workspace.views.${tab}.title`)}</Typography>
          <Typography className="sc-muted">{t(`workspace.views.${tab}.description`)}</Typography>
        </Box>
        {/* ── TAB 0: Dashboard ── */}
        {tab === 0 && (
          <Box
            key="dashboard"
          >
            <ScoutingDashboard
              followedPlayers={followedPlayers}
              watchedMatches={watchedMatches}
              matchNotes={matchNotes}
              agendaMatches={agendaMatches}
              upcomingMatches={upcoming}
              loading={loadingPlayers || loadingDiscovered}
              error={
                playersError
                  ? t('errors.loadPlayers')
                  : discoveredError
                    ? t('errors.loadDiscovered')
                    : null
              }
              onOpenNote={handleOpenMatchNote}
            />
          </Box>
        )}

        {/* ── TAB 1: Jugadores Seguidos ── */}
        {tab === 1 && (
          <Box
            key="players"
          >
            {playersError && (
              <MuiAlert severity="error" sx={{ mb: 2 }}>
                {t('errors.loadPlayers')}
              </MuiAlert>
            )}

            {loadingPlayers ? (
              <Grid container spacing={2}>
                {[1, 2, 3, 4].map((i) => (
                  <Grid item xs={12} sm={6} md={4} key={i}>
                    <Skeleton variant="rectangular" height={160} sx={{ borderRadius: spacing.borderRadius?.md || 2 }} />
                  </Grid>
                ))}
              </Grid>
            ) : followedPlayers.length === 0 ? (
              <ScoutingEmptyState />
            ) : (
              <>
                {/* Search bar */}
                <TextField
                  data-tour="scouting-search"
                  className="sc-player-search"
                  size="small"
                  label={t('players.search')}
                  placeholder={t('players.search')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={{ mb: 3, maxWidth: 360 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ color: colors.neutral[400] }} />
                      </InputAdornment>
                    ),
                  }}
                />

                {filteredPlayers.length === 0 ? (
                  <Typography color={colors.neutral[500]} sx={{ py: 3 }}>
                    {t('players.notFound', { search })}
                  </Typography>
                ) : (
                  <Grid container spacing={2}>
                      {filteredPlayers.map((player) => (
                        <Grid item xs={12} sm={6} md={4} key={player.jugadorId || player.id}>
                          <FollowedPlayerCard
                            player={player}
                            onEditNotes={handleOpenNotes}
                            onUnfollow={handleUnfollow}
                          />
                        </Grid>
                      ))}
                  </Grid>
                )}
              </>
            )}
          </Box>
        )}

        {/* ── TAB 2: Agenda (Próximos Partidos) ── */}
        {tab === 2 && (
          <Box
            key="agenda"
            data-tour="scouting-agenda"
          >
            {discoveredError && (
              <MuiAlert severity="error" sx={{ mb: 2 }}>
                {t('errors.loadAgenda')}
              </MuiAlert>
            )}

            <PlayerFilterChips
              players={followedPlayers}
              selected={playerFilter}
              onChange={setPlayerFilter}
            />

            {!loadingDiscovered && visibleUpcoming.length > 0 && (
              <MuiAlert severity="info" sx={{ mb: 2 }}>
                {t('agenda.limitedInfo')}
              </MuiAlert>
            )}

            {loadingDiscovered ? (
              <Grid container spacing={2}>
                {[1, 2, 3].map((i) => (
                  <Grid item xs={12} sm={6} md={4} key={i}>
                    <Skeleton variant="rectangular" height={140} sx={{ borderRadius: 2 }} />
                  </Grid>
                ))}
              </Grid>
            ) : visibleUpcoming.length === 0 ? (
              <Box className="sc-state" sx={{ textAlign: "center", py: 6 }}>
                <CalendarMonth sx={{ fontSize: 64, color: colors.neutral[300], mb: 2 }} />
                <Typography variant="h6" fontWeight={600} color={colors.neutral[500]}>
                  {t('agenda.noUpcoming')}
                </Typography>
                <Typography variant="body2" color={colors.neutral[400]} sx={{ mt: 1 }}>
                  {followedPlayers.length === 0
                    ? t('agenda.followPrompt')
                    : playerFilter
                      ? t('agenda.noUpcomingPlayer')
                      : t('agenda.noUpcomingTeams')}
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={2}>
                  {visibleUpcoming.map((match, idx) => (
                    <Grid item xs={12} sm={6} md={4} key={match.codPartido || idx}>
                      <DiscoveredMatchCard match={match} navigate={navigate} onOpenNote={handleOpenMatchNote} matchNotesMap={matchNotesMap} watchedSet={watchedSet} onWatch={handleWatchMatch} onUnwatch={handleUnwatchMatch} />
                    </Grid>
                  ))}
              </Grid>
            )}
          </Box>
        )}

        {/* ── TAB 3: Partidos Jugados ── */}
        {tab === 3 && (
          <Box
            key="matches"
          >
            {discoveredError && (
              <MuiAlert severity="error" sx={{ mb: 2 }}>
                {t('errors.loadMatches')}
              </MuiAlert>
            )}

            <PlayerFilterChips
              players={followedPlayers}
              selected={playerFilter}
              onChange={setPlayerFilter}
            />

            {loadingDiscovered ? (
              <Grid container spacing={2}>
                {[1, 2, 3, 4].map((i) => (
                  <Grid item xs={12} sm={6} md={4} key={i}>
                    <Skeleton variant="rectangular" height={140} sx={{ borderRadius: 2 }} />
                  </Grid>
                ))}
              </Grid>
            ) : filteredRecent.length === 0 ? (
              <Box className="sc-state" sx={{ textAlign: "center", py: 6 }}>
                <SportsSoccer sx={{ fontSize: 64, color: colors.neutral[300], mb: 2 }} />
                <Typography variant="h6" fontWeight={600} color={colors.neutral[500]}>
                  {t('matches.noPlayed')}
                </Typography>
                <Typography variant="body2" color={colors.neutral[400]} sx={{ mt: 1 }}>
                  {followedPlayers.length === 0
                    ? t('matches.followPrompt')
                    : playerFilter
                      ? t('matches.noMatchesPlayer')
                      : t('matches.noMatchesTeams')}
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={2}>
                  {filteredRecent.map((match, idx) => (
                    <Grid item xs={12} sm={6} md={4} key={match.codPartido || idx}>
                      <DiscoveredMatchCard match={match} navigate={navigate} onOpenNote={handleOpenMatchNote} matchNotesMap={matchNotesMap} watchedSet={watchedSet} onWatch={handleWatchMatch} onUnwatch={handleUnwatchMatch} />
                    </Grid>
                  ))}
              </Grid>
            )}
          </Box>
        )}

        {/* ── TAB 4: Mi Agenda (Personal Calendar) ── */}
        {tab === 4 && (
          <Box
            key="my-agenda"
            data-tour="scouting-personal-agenda"
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
                <Box>
                  <Typography component="h3" variant="subtitle1" fontWeight={650} sx={{ color: "var(--sc-ink)" }}>
                    {t('myAgenda.title')}
                  </Typography>
                  <Typography variant="body2" className="sc-muted" sx={{ mt: 0.5 }}>
                    {t('workspace.agendaDescription')}
                  </Typography>
                </Box>
                <Chip
                  label={t('workspace.savedMatches', { count: agendaMatches.length })}
                  sx={{
                    fontWeight: 700,
                    backgroundColor: colors.primary[50],
                    color: colors.primary[700],
                  }}
                />
              </Box>

              <ScoutingAgendaView matches={agendaMatches} onUnwatch={handleUnwatchMatch} />
            </Box>
          </Box>
        )}

        {/* ── TAB 5: Notas de Partidos ── */}
        {tab === 5 && (
          <Box
            key="notes"
          >
            <PlayerFilterChips
              players={followedPlayers}
              selected={playerFilter}
              onChange={setPlayerFilter}
            />

            {matchNotes.length === 0 ? (
              <Box className="sc-state" sx={{ textAlign: "center", py: 6 }}>
                <NoteAlt sx={{ fontSize: 64, color: colors.neutral[300], mb: 2 }} />
                <Typography variant="h6" fontWeight={600} color={colors.neutral[500]}>
                  {t('notes.empty')}
                </Typography>
                <Typography variant="body2" color={colors.neutral[400]} sx={{ mt: 1 }}>
                  {t('notes.emptyDesc')}
                </Typography>
              </Box>
            ) : filteredMatchNotes.length === 0 ? (
              <Box className="sc-state" sx={{ textAlign: "center", py: 6 }}>
                <NoteAlt sx={{ fontSize: 64, color: colors.neutral[300], mb: 2 }} />
                <Typography variant="h6" fontWeight={600} color={colors.neutral[500]}>
                  {t('workspace.noPlayerNotes')}
                </Typography>
                <Typography variant="body2" color={colors.neutral[400]} sx={{ mt: 1 }}>
                  {t('notes.emptyDesc')}
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {filteredMatchNotes.map((note) => (
                  <Grid item xs={12} sm={6} md={4} key={note.id}>
                    <Card
                      className="sc-panel sc-note"
                      sx={{
                        height: "100%",
                      }}
                    >
                      <CardActionArea
                        aria-label={t('workspace.openPlayerNote', { name: note.jugadorNombre })}
                      onClick={() => {
                        setMatchNoteMatch({
                          codPartido: note.partidoId,
                          equipoLocal: note.equipoLocal,
                          equipoVisitante: note.equipoVisitante,
                          fechaISO: note.fecha,
                          competicion: note.competicion,
                          codacta: note.partidoId,
                          codEquipoLocal: "", codEquipoVisitante: "", fecha: note.fecha,
                        });
                        setMatchNotePlayer({ jugadorId: note.jugadorId, jugadorNombre: note.jugadorNombre });
                        setMatchNoteOpen(true);
                      }}
                    >
                      <CardContent sx={{ py: 1.5, px: 2, "&:last-child": { pb: 1.5 } }}>
                        {/* Player */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
                          <Star sx={{ fontSize: 16, color: colors.warning?.[500] || "#f59e0b" }} />
                          <Typography variant="body2" fontWeight={700}>
                            {note.jugadorNombre}
                          </Typography>
                        </Box>
                        {/* Match info */}
                        <Typography variant="caption" fontWeight={600} color={colors.neutral[700]} sx={{ display: "block" }}>
                          {note.equipoLocal} vs {note.equipoVisitante}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 0.5, mt: 0.5, mb: 1 }}>
                          {note.fecha && (
                            <Chip
                              label={new Date(note.fecha).toLocaleDateString(i18n.language, { day: "numeric", month: "short", year: "numeric" })}
                              size="small"
                              sx={{ height: 18, fontSize: "0.6rem" }}
                            />
                          )}
                          {note.competicion && (
                            <Chip label={note.competicion} size="small" variant="outlined" sx={{ height: 18, fontSize: "0.6rem" }} />
                          )}
                        </Box>
                        {/* Note preview */}
                        <Typography
                          variant="body2"
                          color={colors.neutral[600]}
                          sx={{
                            whiteSpace: "pre-wrap",
                            overflow: "hidden",
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                            fontSize: "0.8rem",
                            lineHeight: 1.4,
                          }}
                        >
                          {note.notes}
                        </Typography>
                        {/* Updated at */}
                        <Typography variant="caption" color={colors.neutral[400]} sx={{ display: "block", mt: 1, fontSize: "0.6rem" }}>
                          {t('workspace.edited')}: {new Date(note.updatedAt).toLocaleDateString(i18n.language, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                        </Typography>
                      </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        )}
      </Box>

      {/* Player Notes Dialog */}
      <PlayerNotesDialog
        open={notesDialogOpen}
        player={selectedPlayer}
        userId={userId}
        onClose={handleCloseNotes}
      />

      {/* Match Note Dialog */}
      <MatchNoteDialog
        open={matchNoteOpen}
        match={matchNoteMatch}
        player={matchNotePlayer}
        userId={userId}
        existingNote={existingMatchNote}
        onClose={handleCloseMatchNote}
      />
    </Box>
  );
};

export default ScoutingPageModern;
